// External
import { CookieService } from 'ngx-cookie-service';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tv4 } from 'tv4';

// Internal
import { RequestsService } from '../../global/request-service/requests.service';
import { ValuesService } from '../../../../common/values/values.service';
import { schemas } from '../../../../common/models/schemas';
import { ConfigService } from '../../../../common/config/config.service';
import { map, catchError } from 'rxjs/operators';
import { AuthService } from '../../global/auth/auth-service.service';


@Injectable({
    providedIn: 'root'
})

export class ConnectLoginService {

    constructor(
        private readonly cookieService: CookieService,
        private  readonly requestsService: RequestsService,
        private readonly valuesService: ValuesService,
        private readonly configService: ConfigService,
        private readonly authService: AuthService
    ) { }

    /**
     * This method is used to retrive a CONNECT token, which will expire in one day, based on an existing CONNECT token
     * @returns Response from server
     */
    temp_token(): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'get_login_token',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    device_id: this.valuesService.connectDeviceId,
                    app_id: this.valuesService.connectAppId
                }
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                console.error("Error mgmt >> dupa req", error);
                throw error;
            })
        )
    }

    /**
     * This method is used to retrieve a CONNECT token (valid until it is explicitly invalidated using logout method) in exchange of a token generated by a valid authentication service
     * @param {object} opt Contains mandatory `user_token` and optional `type`
     * @returns Response from server with connect token
     */
    connect(opt): Observable<any> {
        //
        if (!tv4.validate(opt, schemas.schemaConnectLogin)) {
            console.error('Invalid params');
            return of('Invalid params');
        }

        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: '',
            params: {
                partner_user_token: opt.user_token,
                partner_id: this.configService.config.partner_id,
                device_id: this.valuesService.connectDeviceId,
                app_id: this.valuesService.connectAppId,
                redirect_uri: `https://${location.host}`,
                client_id: this.configService.prodConfig.clientIds[location.host] ?? ''
            }
        };

        json.method = 'connect';
        if (opt.type) {
            json.params['type'] = opt.type;
        }

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST');
    }

    /**
     * This method is used to obtain temporary token for account.bitdefender.com
     * @returns {user_token} used to login into account
     */
    getMgmtToken(): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'get_management_token',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId
                },
                partner_id: this.configService.config.partner_id
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )

    }

    changePass(data): Observable<any>{
        const json = {
            user: data.email,
            oldpass: data.oldPass,
            lang: data.lang,
            pass: data.newPass,
            partner_id: this.configService.config.partner_id,
            redirect_url: this.configService.getCentralBitdefenderSiteUrl()
        };

        return this.requestsService.make(this.configService.config.loginServer, this.valuesService.change_lbn_service, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )
    }

    askDeleteAccount(): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'ask_delete_account',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId,
                    device_id: this.valuesService.connectDeviceId
                },
                partner_id: this.configService.config.partner_id,
                email: this.cookieService.get(this.valuesService.cookieEmail),
                redirect_url: `${window.location.origin}/${this.valuesService.centralPaths.services.path.concat(this.valuesService.centralPaths.services.delete.path, this.valuesService.centralPaths.services.delete.confirm.path)}`
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )

    }

    deleteAccount(deleteToken): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'delete_account',
            params: {
                connect_source: {
                    user_token: deleteToken,
                    app_id: this.valuesService.connectAppId,
                    device_id: this.valuesService.connectDeviceId
                },
                partner_id: this.configService.config.partner_id,
                email: this.cookieService.get(this.valuesService.cookieEmail)
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                    return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )

    }

    /**
     * This method is used to change user email
     * @returns
     */
    changeEmail(data): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'change_account',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId
                },
                partner_id: this.configService.config.partner_id,
                email: data.email,
                redirect_url: this.configService.getCentralBitdefenderSiteUrl(),
                link: this.configService.getChangeEmailUrl()
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )

    }

    /**
     * This method is used to change user email
     * @returns
     */
     getHasLeaks(): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'get_login_info',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    app_id: this.valuesService.connectAppId
                },
                partner_id: this.configService.config.partner_id,
            }
        };

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.loginMgmtService,
            json,
            'POST'
        ).pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        );
    }

    /**
     * This method is used to login user
     * @returns
     */
    login(data): Observable<any> {
        const json = {
            user: data.user,
            pass: data.pass,
            partner_id: this.configService.config.partner_id,
            redirect_url: this.configService.getCentralBitdefenderSiteUrl()
        };

        return this.requestsService.make(this.configService.config.loginServer, this.valuesService.login_lbn_service, json, 'POST')
        .pipe(
            map(
                (res: any) => {
                return res.result;
                }
            ),
            catchError((error) => {
                throw error;
            }),
        )

    }

    /**
     * This method is used to change langunge in user info inventory
     * @returns {status} if the change was made or not
     */
    setLang(data) {

        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: "set_language",
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    device_id: this.valuesService.connectDeviceId,
                    app_id: this.valuesService.connectAppId
                },
                lang: data.lang,
                partner_id: this.configService.config.partner_id
            }
        };

        return this.requestsService.make(this.configService.config.nimbusServer, this.valuesService.loginMgmtService, json, 'POST')
        // .pipe(
        //     map(
        //         (res: any) => {
        //         return res.result;
        //         }
        //     ),
        //     catchError((error) => {
        //         throw error;
        //     }),
        // )

    };

    /**
     * This method is used to change constext.
     * @param {string} newContextId - The new context id.
     * @returns {Observable<any>} The request response.
     */
    public switchContext(newContextId: string): Observable<any> {
        const json = {
            id: parseInt((Math.random() * 100).toString(), 10),
            jsonrpc: this.valuesService.jsonrpc,
            method: 'switch_context',
            params: {
                connect_source: {
                    user_token: this.cookieService.get(this.valuesService.cookieToken),
                    device_id: this.valuesService.connectDeviceId,
                    app_id: this.valuesService.connectAppId
                },
                context_id: newContextId,
            }
        };

        return this.requestsService.make(
            this.configService.config.nimbusServer,
            this.valuesService.loginMgmtService,
            json,
            'POST'
        ).pipe(
            map((resp: any) => {
                if (resp?.result?.status === 0 && resp?.result?.user_token) {
                    return resp.result.user_token;
                }
                throw resp;
            }),
            catchError((error) => {
                throw error;
            }),
        );
    };
}
